import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleHome } from "@/lib/constants";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import CustomerDashboard from "@/pages/CustomerDashboard";
import RequestDetail from "@/pages/RequestDetail";
import TowDetail from "@/pages/TowDetail";
import WorkshopDashboard from "@/pages/WorkshopDashboard";
import TowDashboard from "@/pages/TowDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import PendingApproval from "@/pages/PendingApproval";
import PaymentReturn from "@/pages/PaymentReturn";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SupportPage from "@/pages/SupportPage";
import SupportDesk from "@/pages/SupportDesk";
import MyInvoices from "@/pages/MyInvoices";
import { RouteSeo } from "@/components/RouteSeo";

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );
}

function Protected({ role, roles, allowPending, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (role && user.role !== role) return <Navigate to={roleHome(user.role)} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={roleHome(user.role)} replace />;
  if (!allowPending && ["workshop", "tow_provider"].includes(user.role) && user.status !== "active") {
    return <PendingApproval />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RouteSeo />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Auth mode="login" />} />
          <Route path="/register" element={<Auth mode="register" />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/app" element={<Protected role="customer"><CustomerDashboard /></Protected>} />
          <Route path="/app/request/:id" element={<Protected role="customer"><RequestDetail /></Protected>} />
          <Route path="/app/tow/:id" element={<Protected role="customer"><TowDetail /></Protected>} />
          <Route path="/payment/return" element={<Protected role="customer"><PaymentReturn /></Protected>} />
          <Route path="/workshop" element={<Protected role="workshop"><WorkshopDashboard /></Protected>} />
          <Route path="/tow" element={<Protected role="tow_provider"><TowDashboard /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
          <Route path="/support" element={<Protected roles={["customer", "workshop", "tow_provider"]} allowPending><SupportPage /></Protected>} />
          <Route path="/invoices" element={<Protected role="customer"><MyInvoices /></Protected>} />
          <Route path="/support-desk" element={<Protected roles={["support", "admin"]}><SupportDesk /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
